<template>
    <div class="refund-details-block">
        <div class="block-header">
            <h3 class="block-title">Canceling your plan today means:</h3>
        </div>

        <div class="info-cards">
            <div class="info-card">
                <div class="info-card-header" style="background-color: #8b0000">
                    <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="info-card-body">
                    <div class="card-title">
                        You will end these services, which are currently working to protect your privacy:
                    </div>
                    <ul class="card-list">
                        <li v-for="benefit in (licenseBenefits || []).slice(1, 5)" :key="benefit">{{ benefit }}</li>
                    </ul>
                    <div class="checkbox-group">
                        <label class="checkbox-label" @click="handleCheckboxClick($event, 'one')">
                            <input v-model="options.one" type="checkbox" class="checkbox-input" @click="preventScroll" />
                            <span class="checkbox-custom"></span>
                            <span class="checkbox-text">I Understand</span>
                        </label>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="info-card-header" style="background-color: #ff8c00">
                    <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="info-card-body">
                    <div class="card-title">
                        You will lose access to these privacy-enhancing features:
                    </div>
                    <ul class="card-list">
                        <li v-for="benefit in (licenseBenefits || []).slice(5, 9)" :key="benefit">{{ benefit }}</li>
                    </ul>
                    <div class="checkbox-group">
                        <label class="checkbox-label" @click="handleCheckboxClick($event, 'two')">
                            <input v-model="options.two" type="checkbox" class="checkbox-input" @click="preventScroll" />
                            <span class="checkbox-custom"></span>
                            <span class="checkbox-text">I Understand</span>
                        </label>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="info-card-header" style="background-color: #CCCC00">
                    <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="info-card-body">
                    <div class="card-title">
                        All the removal requests and opt-outs we're managing for you will expire, so you will likely start seeing:
                    </div>
                    <ul class="card-list">
                        <li v-for="benefit in (licenseBenefits || []).slice(9)" :key="benefit">{{ benefit }}</li>
                        <li v-if="(licenseBenefits || []).length <= 9">Increased telemarketing calls</li>
                        <li v-if="(licenseBenefits || []).length <= 9">More marketing SMS/texts</li>
                        <li v-if="(licenseBenefits || []).length <= 9">Your sensitive contact info being publicly exposed</li>
                    </ul>
                    <div class="checkbox-group">
                        <label class="checkbox-label" @click="handleCheckboxClick($event, 'three')">
                            <input v-model="options.three" type="checkbox" class="checkbox-input" @click="preventScroll" />
                            <span class="checkbox-custom"></span>
                            <span class="checkbox-text">I Understand</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="action-buttons">
            <button class="btn btn-outline" @click="$emit('goToPage', 1)">Back</button>
            <button class="btn btn-primary" @click="continueToNext" :disabled="!allowedToContinue">Continue</button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { LICENSE_BENEFITS } from '~/types/licenses/licenses';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.REFUND_LICENSE);

const emit = defineEmits<{
    goToPage: [page: number];
    close: [];
}>();

const allowedToContinue = ref(false);
const options = ref({
    one: false,
    two: false,
    three: false,
});

// Get the specific benefits for this license type
const licenseBenefits = computed(() => {
    return LICENSE_BENEFITS[dialogRef.value.data.slug];
});

// Watch for changes in options to enable/disable continue button
watch(options, (newOptions) => {
    if (newOptions.one && newOptions.two && newOptions.three) {
        allowedToContinue.value = true;
    } else {
        allowedToContinue.value = false;
    }
}, { deep: true });

const continueToNext = () => {
    if (allowedToContinue.value) {
        emit('goToPage', 3);
    }
};

// Prevent scroll behavior on mobile when clicking checkboxes
const preventScroll = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
};

const handleCheckboxClick = (event: Event, optionKey: 'one' | 'two' | 'three') => {
    event.preventDefault();
    event.stopPropagation();
    
    // Toggle the checkbox value without focusing the input
    options.value[optionKey] = !options.value[optionKey];
};
</script>

<style lang="scss" scoped>
.refund-details-block {
    @apply p-6;
}

.block-header {
    @apply mb-6 text-center;
    
    .block-title {
        @apply text-2xl font-semibold text-gray-900;
    }
}

.info-cards {
    @apply flex flex-col lg:flex-row gap-4 justify-center mb-6 items-center lg:items-stretch;
    @screen lg {
        min-height: 400px;
    }
}

.info-card {
    @apply bg-white rounded-lg shadow-md border border-gray-200 flex flex-col w-64 flex-shrink-0;
    
    @screen lg {
        @apply flex-1 w-auto;
    }
    
    .info-card-header {
        @apply flex items-center justify-center h-20 rounded-t-lg;
        
        i {
            @apply text-white text-3xl;
        }
    }
    
    .info-card-body {
        @apply p-4 flex flex-col justify-between flex-1;
        
        .card-title {
            @apply text-sm font-bold text-gray-900 mb-2;
        }
        
        .card-list {
            @apply text-xs text-gray-700 space-y-1.5 mb-3;
            
            li {
                @apply leading-relaxed;
            }
        }
        
        .checkbox-group {
            @apply mt-4;
            
            .checkbox-label {
                @apply flex items-center cursor-pointer;
                
                .checkbox-input {
                    @apply sr-only;
                }
                
                .checkbox-custom {
                    @apply w-4 h-4 border-2 border-gray-300 rounded flex-shrink-0 mr-2 transition-colors;
                    position: relative;
                    
                    &::after {
                        content: '';
                        @apply absolute inset-0 flex items-center justify-center;
                        opacity: 0;
                        transition: opacity 0.2s;
                    }
                }
                
                .checkbox-input:checked + .checkbox-custom {
                    @apply border-blue-500 bg-blue-500;
                    
                    &::after {
                        opacity: 1;
                        content: '✓';
                        @apply text-white text-xs font-bold;
                    }
                }
                
                .checkbox-text {
                    @apply text-sm text-gray-700;
                }
            }
            

        }
    }
}

.action-buttons {
    @apply flex justify-center gap-3;
    
    .btn {
        @apply px-6 py-2 rounded-lg font-medium transition-all duration-200;
        
        &:disabled {
            @apply opacity-50 cursor-not-allowed;
        }
        
        &.btn-outline {
            @apply border border-gray-300 text-gray-700 bg-white;
            
            &:hover:not(:disabled) {
                @apply border-gray-400 bg-gray-50;
            }
        }
        
        &.btn-primary {
            @apply bg-blue-600 text-white border border-blue-600;
            
            &:hover:not(:disabled) {
                @apply bg-blue-700 border-blue-700;
            }
        }
    }
}

</style> 